import Bugsnag from '@bugsnag/js';
import { __ } from '@99designs/i18n';
import { NormalisedBriefProduct } from '../../../../../components/BriefContext';
import { TopProductsQuery } from './products.generated';

export type Product = TopProductsQuery['topProducts'][0]['product'] & {
    prices: { currency: string; listPrice: { taxed: number; untaxed: number } };
};

export type RecentlySelectedProduct = Omit<
    Product,
    '__typename' | 'categories' | 'services' | 'esMpvId'
>;

const hasProductInCategoryMap = (product: Product) => (v: Product) => v.mpvId === product.mpvId;

const findProductInCategoryMap = (
    categoryMap: Map<string, [Product]>,
    categoryName: string,
    f: any
) => categoryMap.get(categoryName)?.find(f);

export const mapCategoriesToProducts = (
    data: TopProductsQuery
): [Map<string, Product[]>, Map<string, Product>] => {
    const productMap = new Map();
    const categoryMap = new Map();
    const products: Product[] = [];

    data?.topProducts
        .filter((item) => item.product.categories.some((category) => category.type === 'Brief'))
        .forEach((item) => {
            item.product.categories.forEach((category) => {
                // Asking whether this is a defined category for the brief as
                // present in the brief category map
                if (!categoryBriefMap(category.name)) {
                    return;
                }

                const categoryName = category.name;
                const product: Product = {
                    ...item.product,
                    prices: {
                        currency: item.prices.currency,
                        listPrice: {
                            taxed: item.prices.listPrice.taxed,
                            untaxed: item.prices.listPrice.untaxed,
                        },
                    },
                };

                products.push(product);

                if (!categoryMap.has(categoryName)) {
                    categoryMap.set(categoryName, []);
                }

                // Check to see if product exists in categorymap before pushing,
                // prevents any possible duplication of products
                if (
                    !findProductInCategoryMap(
                        categoryMap,
                        categoryName,
                        hasProductInCategoryMap(product)
                    )
                ) {
                    categoryMap.get(categoryName).push(product);
                }

                productMap.set(product.mpvId.toLowerCase(), product);
            });
        });

    const sortedCategoryMap = sortCategoryMapByRank(products, categoryMap);

    return [sortedCategoryMap, productMap];
};

export const transformBriefProductToProduct = (
    briefProduct: NormalisedBriefProduct
): RecentlySelectedProduct => {
    if (!briefProduct.key) {
        Bugsnag.notify(
            new Error(
                'Product key is missing from brief product. This should only be possible for bifrost briefs which should not have product options'
            )
        );
    }
    return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        productKey: briefProduct.key!,
        mpvId: briefProduct.fulfilmentMpvId || '',
        name: `${briefProduct.title}`.replace(__('Custom Design:'), '').trim(),
        prices: {
            currency: briefProduct.pricing.currency,
            listPrice: {
                taxed: briefProduct.pricing.taxed,
                untaxed: briefProduct.pricing.untaxed,
            },
        },
    };
};

export const sortCategoryMapByRank = (
    products: Product[],
    categoryMap: Map<string, Product[]>
): Map<string, Product[]> => {
    const categoriesByRank: { [category: string]: number } = {};

    products.forEach((product) => {
        product.categories.forEach((category) => {
            categoriesByRank[category.name] = category.rank;
        });
    });

    const sortedCategoryEntries = [...categoryMap.entries()].sort(
        ([categoryA], [categoryB]) => categoriesByRank[categoryA] - categoriesByRank[categoryB]
    );

    return new Map(sortedCategoryEntries);
};

export const categoryBriefMap = (category: string) =>
    ({
        SignsPosters: __('Signs & Posters'),
        LabelsStickers: __('Labels & Stickers'),
        Stationery: __('Stationery'),
        Other: __('Other'),
        BusinessCards: __('Business Cards'),
        MarketingMaterials: __('Marketing Materials'),
        PopularProducts: __('Popular Products'),
        Banners: __('Banners'),
        Clothing: __('Clothing'),
        Packaging: __('Packaging'),
        WeddingIllustrations: __('Wedding Illustrations'),
    }[category]);
